import * as jose from "jose";
import { Request } from "./request";
import { useUiStore } from "@/stores/ui";
import { useUserStore } from "@/stores/user";
import { t } from "@/locales/i18n";
import WA from "@/miniapp/tg";
import router from "@/router";
import store, { resetStore } from "@/store";
import { takeBalanceLoadInFlight, currentLoadEpoch } from "@/api/coinService";
import { TG_AUTH_FAIL } from "./constants";
import {
  baseAnalytics,
  getRegistrationAnalyticsData,
} from "@/utils/analytics/base";
import { parseSegmentIdFromTgStartParam } from "@/utils/parseSegmentIdFromTgStartParam";
import { setTokens, clearTokens } from "./tokens";

const URL =
  // eslint-disable-next-line no-undef
  globalThis?.import_meta_env?.VITE_API_ENDPOINT ||
  import.meta.env.VITE_API_ENDPOINT;

// Условие «после tmaAuth пускаем на Dashboard»: успешная авторизация ИЛИ
// demo-режим (TG_AUTH_FAIL — isError=true, но demo-дашборд доступен)
export const canEnterAfterTmaAuth = (res) =>
  !!res && (!res.isError || res.code === TG_AUTH_FAIL);

// Токены в localStorage
const persistTokens = (data) => {
  if (!data?.access_token) {
    console.warn("persistTokens: response has no access_token — skipping save");
    return false;
  }
  setTokens(data);
  return true;
};

// Полный сброс сессии: токены (localStorage) + pinia user + Vuex(auth/coinService)
export const resetSession = () => {
  const { removeAuth } = useUserStore();
  clearTokens();
  removeAuth();
  resetStore();
  const stale = takeBalanceLoadInFlight();
  if (stale) {
    const epoch = currentLoadEpoch();
    stale.finally(() => {
      if (currentLoadEpoch() === epoch) resetStore();
    });
  }
};

export const Auth = {
  twoFa: {
    secret: null,
  },
  captcha: {
    secret: null,
  },
  async tmaAuth() {
    const { showPreloader, hidePreloader, addError } = useUiStore();
    const { setAuth, setUserIdForAuthEmail, setAuthNeedEmail } = useUserStore();

    showPreloader();
    baseAnalytics.authStart();
    const res = await Request.post(
      `${URL}/oauth/tma/telegram`,
      {
        init_data: WA.initData,
        ref_id: parseSegmentIdFromTgStartParam(WA.initDataUnsafe.start_param),
        analytics_data: getRegistrationAnalyticsData(),
      },
      {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
      {
        disablePreloader: true,
      },
    );

    if (!res?.isError) {
      // 2xx без токена — не считаем авторизованным.
      if (!persistTokens(res?.data)) {
        addError({});
        hidePreloader();
        return { ...res, isError: true };
      }
      setUserIdForAuthEmail(null);
      setAuthNeedEmail(false);
      setAuth();
      // authenticate false — откатываем pinia-сессию, иначе isAuth=true висит с
      // пустым auth-store (рассинхрон до следующего 401).
      if (!(await this.authenticate())) {
        resetSession();
        addError({});
        hidePreloader();
        return { ...res, isError: true };
      }
    } else if (res?.code === TG_AUTH_FAIL) {
      baseAnalytics.authFailure();
      setUserIdForAuthEmail(res?.message);
      setAuthNeedEmail(true);
    }
    baseAnalytics.authSuccess();
    hidePreloader();

    return res;
  },
  async refresh() {
    const tokens = Request.getTokens();
    const res = await Request.post(
      `${URL}/auth/refresh`,
      tokens,
      {},
      { disablePreloader: true, skipAuthRefreshGate: true },
    );
    // Сохраняем токены здесь, чтобы вызовы refresh() ВНЕ request.js
    // (authenticate) тоже обновляли localStorage
    if (!res?.isError && res?.data?.access_token) {
      setTokens(res.data);
    }
    return res;
  },
  async authenticate() {
    let tokens = Request.getTokens();
    // Guard: токен не прочитался (storage недоступен в TMA-webview) — не роняем
    // boot decodeJwt(undefined). false → вызывающий (tmaAuth/login) откатит
    // pinia-сессию через resetSession
    if (!tokens?.access_token) {
      console.warn("authenticate: no access token (storage unreadable?)");
      return false;
    }
    let parsedToken = jose.decodeJwt(tokens.access_token);
    const isAccessTokenExpired = parsedToken.exp * 1000 < Date.now();
    if (isAccessTokenExpired) {
      const res = await this.refresh();
      if (res?.isError) {
        console.warn("authenticate: refresh failed");
        return false;
      }
      // Перечитываем свежие токены после refresh
      tokens = Request.getTokens();
      if (!tokens?.access_token) return false;
      parsedToken = jose.decodeJwt(tokens.access_token);
    }

    const { setUserData } = useUserStore();

    setUserData(parsedToken);

    // Зеркалим токены+профиль в auth-service store
    store.dispatch("auth/authenticate", {
      access_token: tokens?.access_token,
      refresh_token: tokens?.refresh_token,
      userData: parsedToken,
    });
    // В TMA нет сабаккаунтов: сразу ставим ид из токена
    store.dispatch("auth/setAccountID", parsedToken.user_id);

    return true;
  },
  async getEndpointApi(url, o) {
    return Request.get(`${URL}${url}`, undefined, o);
  },
  // TODO: будет использоваться в задаче по разлогину
  logout() {
    const { addNotification } = useUiStore();
    addNotification("success", t("auth|logout"));
    resetSession();
    router.replace({ name: "Error" });
  },
};
